import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';
import './NetsuiteStaffing.css';

const NetSuiteStaffingPage = () => {
  const controls = useAnimation();
  const [inView] = useInView();

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const pulseAnimation = {
    hidden: { scale: 0.95, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.6,
        repeat: Infinity,
        repeatType: "reverse",
        repeatDelay: 0.5
      }
    }
  };

  return (
    <div style={{
      overflowX: 'hidden',
      fontFamily: "'Poppins', sans-serif",
      background: '#f9fbfd'
    }}>
      <Helmet>
        <title>NetSuite Staffing Solutions USA | SpireNest Talent</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/services/staffing" />
        <meta name="description" content="Find qualified NetSuite professionals with SpireNest's staffing services. We connect U.S. businesses with top ERP talent for project success." />
        <meta name="keywords" content="NetSuite staffing, NetSuite consultants, NetSuite developers, certified experts, USA, SpireNest" />
        <meta property="og:title" content="NetSuite Staffing Services | Hire Certified Experts | SpireNest" />
        <meta property="og:description" content="Hire top NetSuite consultants, developers, and administrators from SpireNest USA." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/netsuite-staffing" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-staffing.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NetSuite Staffing Services | Hire Certified Experts | SpireNest" />
        <meta name="twitter:description" content="Flexible NetSuite staffing solutions for projects, support, and upgrades." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-staffing.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": "https://spirenest.co/services/staffing",
                "name": "SpireNest | NetSuite Staffing Services",
                "description": "Scale your ERP capabilities with SpireNest's NetSuite staffing services. Access certified NetSuite professionals tailored to your project and business needs.",
                "url": "https://spirenest.co/services/staffing",
                "image": "https://spirenest.co/assets/images/og-home.jpg",
                "publisher": {
                  "@type": "Organization",
                  "name": "SpireNest",
                  "url": "https://spirenest.co/",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://spirenest.co/assets/logo-Cko-LaIl.png"
                  }
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://spirenest.co/services/staffing#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://spirenest.co/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Services",
                    "item": "https://spirenest.co/services/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Staffing",
                    "item": "https://spirenest.co/services/staffing"
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Modern Hero Section with Particles */}
      <section style={{
         background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        color: 'white',
        padding: '2rem 0 6rem',
        position: 'relative',
        overflow: 'hidden',
        minHeight:'92vh'
      }}>

        {/* Particle background */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0.6, 0],
              x: Math.random() * 1000 - 500,
              y: Math.random() * 1000 - 500,
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              position: 'absolute',
              width: '2px',
              height: '2px',
              background: 'white',
              borderRadius: '50%'
            }}
          />
        ))}

        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="text-center text-lg-start">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                style={{ marginBottom: '2rem'}}
              >
                <motion.div variants={fadeInUp}>
                  <Badge
                    pill
                    style={{
                      background: 'linear-gradient(90deg, #f97316 100%)', // Purple to Orange
                      color: 'white',
                      fontSize: '0.8rem',
                      padding: '12px 16px',
                      marginBottom: '2rem',
                      display: 'inline-block',
                      border: 'none'
                    }}
                  >
                    NETSUITE STAFFING SOLUTIONS
                  </Badge>


                </motion.div>

                <motion.h1
                  className="display-3 fw-bold mb-4"
                  style={{
                    textShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    lineHeight: '1.2'
                  }}
                  variants={fadeInUp}
                >
                  Transform Your Business With <span style={{
                    color: '#f97316',
                    textShadow: '0 0 10px rgba(249, 115, 22, 0.5'
                  }}>Elite NetSuite Talent</span>
                </motion.h1>

                <motion.p
                  className="lead mb-4"
                  style={{
                    fontSize: '1.25rem',
                    opacity: 0.9
                  }}
                  variants={fadeInUp}
                >
                  Access the top 1% of certified NetSuite professionals to drive your digital transformation
                </motion.p>

                <motion.div variants={fadeInUp}>
                  <Button
                    variant="light"
                    size="lg"
                    className="rounded-pill px-4 py-3 fw-bold me-3 mb-3"
                    style={{
                      background: 'white',
                      color: '#1a237e',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-3px)';
                      e.target.style.boxShadow = '0 12px 30px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2)';
                    }}
                  >
                    <span style={{ position: 'relative', zIndex: 1 }}>
                    <a href="/contact" className="btn  ">
                    Get Started Today
                    </a>
                    </span>
                    <motion.span
                      initial={{ x: -100, opacity: 0 }}
                      whileHover={{ x: 0, opacity: 0.1 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        position: 'absolute',
                        top: '-50%',
                        left: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'linear-gradient(90deg, transparent, rgba(79, 195, 247, 0.3), transparent)',
                        transform: 'rotate(45deg)'
                      }}
                    />
                  </Button>

                  {/* <Button
                    variant="outline-light"
                    size="lg"
                    className="rounded-pill px-4 py-3 fw-bold mb-3"
                    style={{
                      borderWidth: '2px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-3px)';
                      e.target.style.background = 'rgba(255,255,255,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.background = 'transparent';
                    }}
                  >
                    Learn More
                  </Button> */}
                </motion.div>
              </motion.div>
            </Col>

            <Col lg={6} className="mt-5 mt-lg-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                style={{
                  position: 'relative',
                  height: '400px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Floating 3D card effect */}
                <motion.div
                  animate={{
                    y: [0, -20, 0],
                    rotateZ: [0, 2, -2, 0]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  style={{
                    position: 'absolute',
                    width: '300px',
                    height: '180px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(5px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                    transform: 'rotateY(20deg) rotateX(10deg)',
                    zIndex: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                >

                </motion.div>

                <motion.div
                  animate={{
                    y: [0, 20, 0],
                    rotateZ: [0, -3, 3, 0]
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                  style={{
                    position: 'absolute',
                    width: '250px',
                    height: '150px',
                    background: 'rgba(79, 195, 247, 0.1)',
                    borderRadius: '16px',
                    border: '1px solid rgba(79, 195, 247, 0.3)',
                    backdropFilter: 'blur(5px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                    transform: 'rotateY(-15deg) rotateX(5deg)',
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'white',
                    fontSize: '1.2rem'
                  }}
                >

                </motion.div>

                <motion.div
                  animate={{
                    y: [0, -15, 0],
                    rotateZ: [0, 4, -4, 0]
                  }}
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2
                  }}
                  style={{
                    position: 'absolute',
                    width: '200px',
                    height: '120px',
                    background: 'rgba(102, 187, 106, 0.1)',
                    borderRadius: '16px',
                    border: '1px solid rgba(102, 187, 106, 0.3)',
                    backdropFilter: 'blur(5px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                    transform: 'rotateY(10deg) rotateX(-5deg)',
                    zIndex: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                >
                  Right-Fit Professionals
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Floating Talent Benefits Section */}
      <section style={{ padding: '6rem 0', position: 'relative' }}>
        {/* Floating background elements */}
        <motion.div
          animate={{
            x: [0, 20, 0],
            y: [0, -20, 0]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(79, 195, 247, 0.1) 0%, transparent 70%)',
            zIndex: 0
          }}
        />

        <motion.div
          animate={{
            x: [0, -30, 0],
            y: [0, 30, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            bottom: '15%',
            right: '10%',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102, 187, 106, 0.1) 0%, transparent 70%)',
            zIndex: 0
          }}
        />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="fw-bold mb-3" style={{
                color: '#1a237e',
                fontSize: '2.5rem'
              }}>
                Why Choose Our <span style={{ color: '#4fc3f7' }}>NetSuite Talent</span>
              </h2>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <p className="lead" style={{
                color: '#5c6bc0',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                We connect you with the most skilled NetSuite professionals to transform your business operations
              </p>
            </motion.div>
          </motion.div>

          <Row className="g-4 justify-content-center">
            {[
              {
                title: "Top 1% Talent",
                content: "Only the most qualified NetSuite experts pass our rigorous screening process",
                icon: "bi-trophy",
                color: "#4fc3f7"
              },
              {
                title: "90-Day Guarantee",
                content: "We're confident in our matches - we offer a 90-day replacement guarantee",
                icon: "bi-shield-check",
                color: "#66bb6a"
              },
              {
                title: "Fast Placement",
                content: "Average time to fill positions is 72 hours from initial contact",
                icon: "bi-lightning",
                color: "#ffa726"
              },
              {
                title: "Industry Specialists",
                content: "Experts with deep knowledge in your specific industry vertical",
                icon: "bi-building",
                color: "#ec407a"
              },
              {
                title: "Cost Transparency",
                content: "Clear pricing with no hidden fees or surprise charges",
                icon: "bi-cash-stack",
                color: "#ab47bc"
              },
              {
                title: "Ongoing Support",
                content: "Dedicated account manager for continuous support",
                icon: "bi-headset",
                color: "#26c6da"
              }
            ].map((item, index) => (
              <Col md={6} lg={4} key={index}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className="h-100 border-0 rounded-4 overflow-hidden"
                    style={{
                      background: 'white',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1)',
                      cursor: 'pointer'
                    }}
                    whileHover={{
                      y: -10,
                      boxShadow: '0 15px 40px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Card.Body className="p-4">
                      <div
                        className="d-flex align-items-center justify-content-center mb-3"
                        style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '20px',
                          background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}80 100%)`,
                          color: 'white',
                          fontSize: '1.8rem',
                          boxShadow: `0 8px 20px ${item.color}40`
                        }}
                      >
                        <i className={`bi ${item.icon}`}></i>
                      </div>
                      <Card.Title style={{
                        color: '#1a237e',
                        fontSize: '1.4rem',
                        marginBottom: '1rem'
                      }}>
                        {item.title}
                      </Card.Title>
                      <Card.Text style={{ color: '#5c6bc0' }}>
                        {item.content}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Creative Process Section with Parallax Effect */}
      <section style={{
        padding: '6rem 0',
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Floating tech elements */}
        <motion.div
          animate={{
            rotate: 360
          }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '100px',
            height: '100px',
            background: 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEyLDIwQTgsOCAwIDAsMSA0LDEyQTgsOCAwIDAsMSAxMiw0QTgsOCAwIDAsMSAyMCwxMkE4LDggMCAwLDEgMTIsMjBNMTIsMkExMCwxMCAwIDAsMCAyLDEyQTEwLDEwIDAgMCwwIDEyLDIyQTEwLDEwIDAgMCwwIDIyLDEyQTEwLDEwIDAgMCwwIDEyLDJaIiBmaWxsPSJ3aGl0ZSIgZmlsbC1vcGFjaXR5PSIwLjEiLz48L3N2Zz4=")',
            opacity: 0.5
          }}
        />

        <motion.div
          animate={{
            rotate: -360
          }}
          transition={{
            duration: 80,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            position: 'absolute',
            bottom: '30%',
            right: '15%',
            width: '150px',
            height: '150px',
            background: 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEyLDIwQTgsOCAwIDAsMSA0LDEyQTgsOCAwIDAsMSAxMiw0QTgsOCAwIDAsMSAyMCwxMkE4LDggMCAwLDEgMTIsMjBNMTIsMkExMCwxMCAwIDAsMCAyLDEyQTEwLDEwIDAgMCwwIDEyLDIyQTEwLDEwIDAgMCwwIDIyLDEyQTEwLDEwIDAgMCwwIDEyLDJaIiBmaWxsPSJ3aGl0ZSIgZmlsbC1vcGFjaXR5PSIwLjEiLz48L3N2Zz4=")',
            opacity: 0.5
          }}
        />

        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.div variants={fadeInUp}>
              <Badge pill bg="primary" className="mb-3" style={{
                fontSize: '0.8rem',
                padding: '0.5rem 1rem',
                background: 'rgba(255,255,255,0.2)',
                fontWeight: '500'
              }}>
                OUR PROCESS
              </Badge>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <h2 className="fw-bold mb-3" style={{
                fontSize: '2.5rem',
                textShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}>
                How We Deliver <span style={{ color: '#4fc3f7' }}>Exceptional Talent</span>
              </h2>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <p className="lead" style={{
                opacity: 0.9,
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                Our proven 4-step process ensures you get the perfect NetSuite professionals
              </p>
            </motion.div>
          </motion.div>

          {/* Creative Process Timeline */}
          <div style={{ position: 'relative', marginTop: '4rem' }} className="process-timeline">
            {/* Connecting line */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '0',
              bottom: '0',
              width: '4px',
              background: 'linear-gradient(to bottom, #4fc3f7, #66bb6a)',
              transform: 'translateX(-50%)',
              zIndex: 1,
              borderRadius: '2px'
            }} className="timeline-line"></div>

            {[
              {
                step: 1,
                title: "Understanding Your Needs ",
                description: "We assess your specific staffing requirements, industry needs, and project goals.",
                icon: "bi-search",
                color: "#4fc3f7"
              },
              {
                step: 2,
                title: "Talent Sourcing & Screening ",
                description: "Our team identifies, screens, and shortlists top NetSuite professionals.",
                icon: "bi-people",
                color: "#66bb6a"
              },
              {
                step: 3,
                title: "Candidate Selection ",
                description: "You interview and select the best candidates that fit your business needs. ",
                icon: "bi-check-circle",
                color: "#ffa726"
              },
              {
                step: 4,
                title: "Onboarding & Support ",
                description: "We ensure a smooth onboarding process and provide continued support as needed. ",
                icon: "bi-rocket",
                color: "#ec407a"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`process-item ${index % 2 === 0 ? 'process-left' : 'process-right'}`}
                style={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: index % 2 === 0 ? 'flex-start' : 'flex-end',
                  marginBottom: '4rem',
                  zIndex: 2
                }}
              >
                <div
                  className="process-card-wrapper"
                  style={{
                    width: 'calc(50% - 40px)',
                    maxWidth: '500px',
                    position: 'relative'
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '16px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      padding: '2rem',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      width: '5px',
                      height: '100%',
                      background: item.color
                    }}></div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '16px',
                        background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}80 100%)`,
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '1.5rem',
                        marginRight: '1.5rem',
                        flexShrink: 0,
                        boxShadow: `0 8px 20px ${item.color}40`
                      }}>
                        <i className={`bi ${item.icon}`}></i>
                      </div>
                      <div>
                        <div style={{
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: item.color,
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}>
                          Step {item.step}
                        </div>
                        <h4 style={{
                          color: 'white',
                          marginBottom: '0.5rem',
                          fontSize: '1.5rem'
                        }}>
                          {item.title}
                        </h4>
                      </div>
                    </div>
                    <p style={{
                      color: 'rgba(255,255,255,0.8)',
                      paddingLeft: '7.5rem'
                    }}>
                      {item.description}
                    </p>
                  </motion.div>
                </div>

                {/* Step connector */}
                <div style={{
                  position: 'absolute',
                  top: '50px',
                  [index % 2 === 0 ? 'right' : 'left']: '-20px',
                  width: '40px',
                  height: '3px',
                  background: item.color,
                  zIndex: 3
                }}></div>

                {/* Step number on timeline */}
                <div style={{
                  position: 'absolute',
                  top: '40px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: '#1a237e',
                  border: `3px solid ${item.color}`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                  zIndex: 4
                }}>
                  {item.step}
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Interactive Stats Section */}
      {/* <section style={{ padding: '6rem 0', background: 'white' }}>
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="fw-bold mb-3" style={{
                color: '#1a237e',
                fontSize: '2.5rem'
              }}>
                Our <span style={{ color: '#4fc3f7' }}>Results</span> Speak For Themselves
              </h2>
            </motion.div>
          </motion.div>

          <Row className="g-4">
            {[
              {
                value: "98%",
                label: "Client Satisfaction",
                description: "Of clients rate our service as excellent",
                color: "#4fc3f7",
                icon: "bi-emoji-smile"
              },
              {
                value: "72h",
                label: "Average Placement",
                description: "Time to fill most NetSuite positions",
                color: "#66bb6a",
                icon: "bi-speedometer2"
              },
              {
                value: "300+",
                label: "Experts Available",
                description: "Certified NetSuite professionals in our network",
                color: "#ffa726",
                icon: "bi-people"
              },
              {
                value: "12+",
                label: "Years Experience",
                description: "Specializing in NetSuite staffing solutions",
                color: "#ec407a",
                icon: "bi-award"
              }
            ].map((stat, index) => (
              <Col lg={3} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    textAlign: 'center',
                    padding: '2rem',
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                    height: '100%',
                    borderBottom: `4px solid ${stat.color}`,
                    transition: 'all 0.4s ease'
                  }}
                  whileHover={{
                    y: -10,
                    boxShadow: `0 15px 40px ${stat.color}20`
                  }}
                >
                  <div style={{
                    fontSize: '3.5rem',
                    fontWeight: '700',
                    color: stat.color,
                    marginBottom: '0.5rem',
                    lineHeight: '1'
                  }}>
                    {stat.value}
                  </div>
                  <h4 style={{
                    color: '#1a237e',
                    marginBottom: '1rem'
                  }}>
                    {stat.label}
                  </h4>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#5c6bc0',
                    marginBottom: '1rem'
                  }}>
                    <i className={`bi ${stat.icon} me-2`} style={{ color: stat.color }}></i>
                    <span>{stat.description}</span>
                  </div>
                  <motion.div
                    variants={pulseAnimation}
                    style={{
                      width: '40px',
                      height: '4px',
                      background: stat.color,
                      borderRadius: '2px',
                      margin: '0 auto'
                    }}
                  />
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section> */}

      {/* Animated CTA Section */}
      <section style={{
        padding: '6rem 0',
        background: 'gray',
        color: 'black',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(79, 195, 247, 0.3) 0%, transparent 70%)',
            zIndex: 0
          }}
        />

        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.15, 0.1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          style={{
            position: 'absolute',
            bottom: '15%',
            right: '15%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102, 187, 106, 0.3) 0%, transparent 70%)',
            zIndex: 0
          }}
        />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="display-5 fw-bold mb-4">
                Ready to Transform Your NetSuite Team?
              </h2>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <p className="lead mb-5" style={{ fontSize: '1.25rem', opacity: 0.9 }}>
                Let's discuss how we can help you find the perfect NetSuite talent today
              </p>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Button
                variant="light"
                size="lg"
                className="rounded-pill px-4 py-3 fw-bold me-3 mb-3"
                style={{
                  background: 'black',
                  color: '#1a237e',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 12px 30px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2)';
                }}
              >
                <span style={{ position: 'relative', zIndex: 1 }}>
                <a href="/contact" >
                    Get Started Today
                </a>
                </span>
                <motion.span
                  initial={{ x: -100, opacity: 0 }}
                  whileHover={{ x: 0, opacity: 0.1 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'linear-gradient(90deg, transparent, rgba(79, 195, 247, 0.3), transparent)',
                    transform: 'rotate(45deg)'
                  }}
                />
              </Button>

              {/* <Button
                variant="outline-light"
                size="lg"
                className="rounded-pill px-4 py-3 fw-bold mb-3"
                style={{
                  borderWidth: '2px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'transparent';
                }}
              >
                Schedule a Call
              </Button> */}
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        .card-hover-effect {
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1);
        }
        
        .card-hover-effect:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 15px 40px rgba(0,0,0,0.1) !important;
        }
      `}</style>
    </div>
  );
};

export default NetSuiteStaffingPage;